import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CheckCircle, XCircle, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await authAPI.verifyResetToken(token);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={48} className="animate-spin" style={{ color: '#8B0A3A', margin: '0 auto 16px' }} />
          <p style={{ color: '#64748b' }}>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
        <div style={{ maxWidth: '400px', margin: '0 auto', paddingTop: '60px' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '40px 32px', 
            borderRadius: '16px', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            textAlign: 'center'
          }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#dcfce7', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <ShieldCheck size={40} color="#16a34a" />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a', marginBottom: '12px' }}>
              Password Reset Successful!
            </h1>
            <p style={{ color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
              Your password has been changed successfully. You can now login with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                width: '100%',
                padding: '14px 24px',
                backgroundColor: '#8B0A3A',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: 'bold',
                fontSize: '14px',
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!token || !tokenValid) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
        <div style={{ maxWidth: '400px', margin: '0 auto', paddingTop: '60px' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '40px 32px', 
            borderRadius: '16px', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            textAlign: 'center'
          }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#fee2e2', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <XCircle size={40} color="#dc2626" />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626', marginBottom: '12px' }}>
              Invalid or Expired Link
            </h1>
            <p style={{ color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link 
              to="/forgot-password" 
              style={{ 
                display: 'inline-block',
                width: '100%',
                padding: '14px 24px',
                backgroundColor: '#f97316',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '10px',
                fontWeight: 'bold',
                textAlign: 'center',
                boxSizing: 'border-box'
              }}
            >
              Request New Link
            </Link>
            <p style={{ marginTop: '16px' }}>
              <Link to="/login" style={{ color: '#8B0A3A', fontWeight: 'bold' }}>
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
      <div style={{ maxWidth: '400px', margin: '0 auto', paddingTop: '40px' }}>
        <Link to="/login" style={{ display: 'flex', alignItems: 'center', color: '#64748b', marginBottom: '20px', textDecoration: 'none' }}>
          <ArrowLeft style={{ marginRight: '8px', width: '16px', height: '16px' }} />
          Back to login
        </Link>
        
        <div style={{ 
          backgroundColor: 'white', 
          padding: '32px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <div style={{ 
            width: '64px', 
            height: '64px', 
            borderRadius: '16px', 
            backgroundColor: '#dbeafe', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Lock size={32} color="#2563eb" />
          </div>
          
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#8B0A3A', marginBottom: '8px', textAlign: 'center' }}>
            Create New Password
          </h1>
          <p style={{ color: '#64748b', marginBottom: '24px', textAlign: 'center', fontSize: '14px' }}>
            Enter a new password for <strong>{email}</strong>
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '0 48px 0 16px',
                    fontSize: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '0 48px 0 16px',
                    fontSize: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>
                  Passwords do not match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || password !== confirmPassword}
              style={{
                width: '100%',
                height: '48px',
                backgroundColor: (loading || password !== confirmPassword) ? '#d1d5db' : '#8B0A3A',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '14px',
                textTransform: 'uppercase',
                border: 'none',
                borderRadius: '10px',
                cursor: (loading || password !== confirmPassword) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
